// Direct ads on the City Politics network (citypolitics.org): what the Worker (worker/src/admarket.js), the slots in the
// browser (site/js/ad-slots.js) and the campaign builder (dashboard-src/admarket/) all have to agree on. No DOM and no
// storage here except where a function is handed one, so the tests run it as is.
//
//   placements and formats     what an advertiser buys, and which slots (page types, units) each covers
//   quote()                    the price, from site/data/ad-pricing.json and the measured inventory
//   validHttpsUrl()            the only click destinations we redirect to
//   validateImage()            the upload rules (types, bytes, dimensions)
//   transition()               a campaign's states
//   campaignMatches()          whether a live campaign fits a slot (site, page type, district, article, unit)
//   abVariant()                the reader's A/B half, sticky in localStorage, no identifier
//   directAdHtml()             the markup of a direct ad (the slot and the builder's preview draw the same thing)

import { slotTypeOf } from './ad-fill.js';
import { NO_AD_ARTICLE, AD_LABEL } from './ad-slots.js';
import { PROMO_UNITS } from './house-ad.js';

export { AD_LABEL };

// ---------- what's for sale ----------

/** Page types (site/js/ad-fill.js SLOT_TYPES) by placement. Home and "other" pages carry no slots. */
export const PLACEMENTS = {
  sitewide: { label: 'Site-wide', desc: 'Every page with an ad slot: articles, data pages, meetings and, in Chicago, the election guide. On a partner site, every page carrying our ad tag.', types: ['article', 'ward', 'matter', 'meeting', 'meetings', 'election', 'partner'] },
  article: { label: 'In-article', desc: 'Inside On My Block articles, between paragraphs.', types: ['article'] },
  data: { label: 'Data pages', desc: 'Ward and district, member, legislation (matter) and meeting pages.', types: ['ward', 'matter', 'meeting'] },
  election: { label: 'Election guide', desc: "Chicago's 2027 election guide and candidates' pages.", types: ['election'], chicagoOnly: true },
  meetings: { label: 'Meetings list', desc: 'The City Council meetings page.', types: ['meetings'], cityOnly: true },
};
export const PLACEMENT_IDS = Object.keys(PLACEMENTS);
export const PAGE_TYPE_LABELS = { article: 'On My Block articles', ward: 'Ward, district and member pages', matter: 'Legislation pages', meeting: 'Meeting pages', meetings: 'Meetings list', election: 'Election guide', partner: 'Partner sites' };

/** Which placements a site offers: the election guide is Chicago's, a state site has no meetings or data pages yet. */
export function placementsFor(site) {
  if (site?.entityType === 'partner') return ['sitewide'];   // an outside site: its tags' zones, every page
  const state = site?.entityType === 'state';
  return PLACEMENT_IDS.filter((p) => {
    if (PLACEMENTS[p].chicagoOnly) return site?.slug === 'chicago';
    if (state && (p === 'data' || p === 'meetings')) return false;
    return true;
  });
}

/**
 * Formats: the banner sizes our slots already hold, and a native card. `units`: the slot units it fills (ad-slots.js
 * AD_SIZES). A leaderboard set fills the leaderboard slot at the size the reader's screen takes (728x90, 468x60, 320x50).
 */
export const FORMATS = {
  leaderboard: { label: 'Leaderboard set', desc: '728x90 for desktops and 320x50 for phones (468x60 optional, for narrow columns).', kind: 'banner', sizes: ['728x90', '468x60', '320x50'], required: ['728x90', '320x50'], units: ['728x90', '468x60', '320x50'] },
  '300x250': { label: 'Medium rectangle', desc: '300x250, between sections and in articles.', kind: 'banner', sizes: ['300x250'], required: ['300x250'], units: ['300x250'] },
  native: { label: 'Native card', desc: 'An image, a headline, a line of text and your link, in the first in-article slot and native slots.', kind: 'native', sizes: ['native'], required: ['native'], units: ['native'] },
};
export const FORMAT_IDS = Object.keys(FORMATS);

/** The slot units each format can fill. */
export const formatForUnit = (unit) => FORMAT_IDS.find((f) => FORMATS[f].units.includes(unit)) || null;

// ---------- uploads ----------

export const IMAGE_TYPES = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };
export const IMAGE_RULES = {
  maxBytes: { banner: 150_000, native: 400_000 },
  native: { minWidth: 600, maxWidth: 2400, minRatio: 1.5, maxRatio: 2.1 },
};
export const TEXT_LIMITS = { name: 80, headline: 60, body: 140, sponsor: 40, paidForBy: 120, payerName: 120, payerAddress: 300, reason: 500 };

/** The size of a banner ("728x90") as numbers. */
export const sizeOf = (s) => { const m = /^(\d+)x(\d+)$/.exec(String(s)); return m ? { width: Number(m[1]), height: Number(m[2]) } : null; };

/**
 * What's wrong with an image for `size` (a banner size, or "native"), or null. Banners: exactly that size, or exactly
 * twice it (for sharp screens). Native: 600 to 2400 pixels wide, 1.5:1 to 2.1:1 (1200x628 is the usual).
 */
export function validateImage({ type, bytes, width, height } = {}, size) {
  if (!IMAGE_TYPES[type]) return 'Use a PNG, JPEG, GIF or WebP image.';
  const native = size === 'native';
  const max = native ? IMAGE_RULES.maxBytes.native : IMAGE_RULES.maxBytes.banner;
  if (!Number.isFinite(bytes) || bytes <= 0) return 'The file is empty.';
  if (bytes > max) return `The file is ${Math.ceil(bytes / 1000)} KB; the limit is ${max / 1000} KB.`;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) return "Couldn't read the image's size.";
  if (native) {
    const r = width / height, R = IMAGE_RULES.native;
    if (width < R.minWidth || width > R.maxWidth) return `A native image is ${R.minWidth} to ${R.maxWidth} pixels wide (this one is ${width}).`;
    if (r < R.minRatio || r > R.maxRatio) return `A native image is landscape, about 1.91:1 (1200x628); this one is ${width}x${height}.`;
    return null;
  }
  const s = sizeOf(size);
  if (!s) return 'Unknown size.';
  if ((width === s.width && height === s.height) || (width === s.width * 2 && height === s.height * 2)) return null;
  return `This slot is ${size}: upload ${size} or ${s.width * 2}x${s.height * 2}; this image is ${width}x${height}.`;
}

/** An image's type and pixel size from its first bytes (PNG, GIF, JPEG, WebP), or null. Never trusts the file name. */
export function sniffImage(buf) {
  const b = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const u16 = (i) => (b[i] << 8) | b[i + 1], le16 = (i) => b[i] | (b[i + 1] << 8), u32 = (i) => ((b[i] << 24) >>> 0) + (b[i + 1] << 16) + (b[i + 2] << 8) + b[i + 3];
  const ascii = (i, n) => String.fromCharCode(...b.slice(i, i + n));
  if (b.length >= 24 && b[0] === 0x89 && ascii(1, 3) === 'PNG') return { type: 'image/png', width: u32(16), height: u32(20) };
  if (b.length >= 10 && ascii(0, 3) === 'GIF') return { type: 'image/gif', width: le16(6), height: le16(8) };
  if (b.length >= 30 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    const kind = ascii(12, 4);
    if (kind === 'VP8 ') return { type: 'image/webp', width: le16(26) & 0x3fff, height: le16(28) & 0x3fff };
    if (kind === 'VP8L') { const x = b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24); return { type: 'image/webp', width: (x & 0x3fff) + 1, height: ((x >> 14) & 0x3fff) + 1 }; }
    if (kind === 'VP8X') return { type: 'image/webp', width: 1 + (b[24] | (b[25] << 8) | (b[26] << 16)), height: 1 + (b[27] | (b[28] << 8) | (b[29] << 16)) };
    return null;
  }
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8) {
    let i = 2;
    while (i + 9 < b.length) {
      if (b[i] !== 0xff) { i += 1; continue; }
      const m = b[i + 1];
      if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { i += 2; continue; }
      const len = u16(i + 2);
      if ((m >= 0xc0 && m <= 0xc3) || (m >= 0xc5 && m <= 0xc7) || (m >= 0xc9 && m <= 0xcb) || (m >= 0xcd && m <= 0xcf)) return { type: 'image/jpeg', width: u16(i + 7), height: u16(i + 5) };
      i += 2 + len;
    }
  }
  return null;
}

// ---------- links ----------

/**
 * The advertiser's link, if we will redirect to it: https only, a real host name (no IP address, no localhost), no user
 * name or password in it, at most 2000 characters, and not one of our own ad redirects. Returns the normalized URL or null.
 */
export function validHttpsUrl(raw) {
  const s = String(raw ?? '').trim();
  if (!s || s.length > 2000 || /[\s<>"'`\\]/.test(s)) return null;
  let u;
  try { u = new URL(s); } catch { return null; }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return null;
  const host = u.hostname.toLowerCase();
  if (!/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(host)) return null;   // a name, not an IP
  if (/(^|\.)(localhost|local|internal|invalid|test|example)$/.test(host)) return null;
  if (/^\/ads\/(click|img)\//.test(u.pathname)) return null;
  return u.toString();
}

/** The host to show under a native card ("example.com"). */
export const displayHost = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; } };

// ---------- dates ----------

const DAY = 86_400_000;
export const isoDay = (t = Date.now()) => new Date(t).toISOString().slice(0, 10);
const dayMs = (d) => Date.parse(`${d}T00:00:00Z`);
export const validDay = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d)) && Number.isFinite(dayMs(d)) && isoDay(dayMs(d)) === d;
/** Days in a flight, both ends counted. */
export const flightDays = (start, end) => (validDay(start) && validDay(end) ? Math.round((dayMs(end) - dayMs(start)) / DAY) + 1 : 0);
/** The last day of a flight of `weeks` weeks from `start`. */
export const endForWeeks = (start, weeks) => isoDay(dayMs(start) + (Math.max(1, Math.round(weeks)) * 7 - 1) * DAY);
export const addDays = (d, n) => isoDay(dayMs(d) + n * DAY);

// ---------- the quote ----------

/** A calendar month later (the 31st of a short month is its last day). */
export function addMonths(d, n) {
  const t = new Date(dayMs(d));
  const y = t.getUTCFullYear(), m = t.getUTCMonth() + Math.round(n), day = t.getUTCDate();
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return isoDay(Date.UTC(y, m, Math.min(day, last)));
}
/** A sponsorship's last day: `periods` weeks, or calendar months, from its start. */
export const sponsorshipEnd = (start, period, periods) => (period === 'month' ? addDays(addMonths(start, Math.max(1, Math.round(periods))), -1) : endForWeeks(start, periods));

/** The four ways to buy (ad-pricing.json `models`, all on by default). */
export const MODELS = {
  cpm: { label: 'Impressions over dates', short: 'CPM', desc: 'Your dates, priced by the viewable impressions we estimate (CPM), at least the weekly minimum. Delivery is paced evenly.' },
  block: { label: 'Impression block', short: 'Block', desc: 'A fixed number of viewable impressions, delivered as fast as traffic allows. Bigger blocks cost less per thousand.' },
  cpc: { label: 'Cost per click', short: 'CPC', desc: 'Pay only for valid clicks, at your bid, up to a daily and a total budget you prepay. Unspent budget is credited or refunded.' },
  sponsorship: { label: 'Sponsorship', short: 'Sponsorship', desc: 'One slot, yours alone, for a week or a month at a flat rate: every page of the placement shows your ad there.' },
};
export const MODEL_IDS = Object.keys(MODELS);
export const modelsOn = (pricing) => MODEL_IDS.filter((m) => pricing?.models?.[m] !== false);

const cents = (dollars) => Math.round(dollars * 100);
const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
/**
 * A site's rates for a placement, from the pricing file (the site's own entry, else the default): CPM and weekly
 * minimum, the CPC floor, and the sponsorship rates for a week and a month.
 */
export function rateFor(pricing, site, placement) {
  const own = pricing?.sites?.[site] || {}, def = pricing?.default || {};
  const pick = (k) => num(own[k]?.[placement] ?? def[k]?.[placement] ?? 0);
  return {
    cpm: pick('cpm'), weeklyMinimum: pick('weeklyMinimum'), cpcFloor: pick('cpcFloor'),
    sponsorWeek: num(own.sponsorship?.week?.[placement] ?? def.sponsorship?.week?.[placement] ?? 0),
    sponsorMonth: num(own.sponsorship?.month?.[placement] ?? def.sponsorship?.month?.[placement] ?? 0),
  };
}
/** An impression block's discount: the percent for the largest size at or below it. */
export function blockDiscount(pricing, size) {
  let best = 0, at = -1;
  for (const [k, p] of Object.entries(pricing?.impressionBlocks?.discountPercent || {})) if (Number(k) <= size && Number(k) > at) { at = Number(k); best = num(p); }
  return best;
}
/** A CPC campaign's floor: the highest floor among its sites, in cents. */
export const cpcFloorCents = (pricing, sites, placement) => Math.max(0, ...(sites || []).map((s) => cents(rateFor(pricing, s, placement).cpcFloor)));

const TARGETING_LABELS = { types: 'Page types', articles: 'Articles', districts: 'Wards or districts', regions: 'States', cities: 'Cities', zips: 'ZIP codes' };
/**
 * The targeting premium (pricing.targeting): each kind of targeting the order uses adds its percent, location once at
 * its finest level (ZIP codes, else cities, else states), capped at maxPercent. {percent, parts: [{key, label, percent}]}.
 *   targeting: { types, districts, articles, geo: { regions, cities, zips } }
 */
export function targetingPremium(pricing, targeting) {
  const pct = pricing?.targeting?.percent || {}, t = targeting || {}, g = t.geo || {};
  const used = [];
  if (t.types?.length) used.push('types');
  if (t.articles?.length) used.push('articles');
  if (t.districts?.length) used.push('districts');
  const geo = g.zips?.length ? 'zips' : g.cities?.length ? 'cities' : g.regions?.length ? 'regions' : null;
  if (geo) used.push(geo);
  const parts = used.map((key) => ({ key, label: TARGETING_LABELS[key], percent: num(pct[key]) })).filter((x) => x.percent > 0);
  const sum = parts.reduce((n, x) => n + x.percent, 0);
  const max = pricing?.targeting?.maxPercent;
  return { percent: max != null ? Math.min(sum, num(max)) : sum, parts };
}

/** A CPC campaign's floor with the targeting premium, in cents (what quote checks a bid against). */
export const targetedCpcFloorCents = (pricing, sites, placement, targeting) => Math.round(cpcFloorCents(pricing, sites, placement) * (1 + targetingPremium(pricing, targeting).percent / 100));

/**
 * The price of a campaign, in cents, by its model (order.model, 'cpm' by default):
 *   cpm          per site: days x the estimated daily impressions x CPM / 1000, or the weekly minimum x weeks if more
 *   block        order.block viewable impressions, split across the sites by their measured traffic, at each site's
 *                CPM less the block's size discount; the end date is when the traffic should have delivered it
 *                (plus impressionBlocks.slackPercent)
 *   cpc          the prepaid total budget (order.budget dollars; order.daily a day, else spread evenly); order.bid a
 *                click, at least the sites' CPC floor
 *   sponsorship  per site: the week or month rate x order.periods; the end date follows from the start
 * Then the targeting premium (targetingPremium; on the subtotal, or on the CPC floor), the network bundle's discount
 * (every site in the network; not on CPC budgets), the A/B add-on (a percent of the total, with a floor) and any
 * political surcharge. `problems`: what makes the order impossible as it stands.
 * `plan`: what the ad engine needs to run it (stored with the campaign).
 *
 *   order: { model, sites: [slug], placement, start, end, ab, political, block, bid, budget, daily, period, periods,
 *            targeting: { types, districts, articles, geo } }
 *   estimates: { [site]: daily viewable impressions for this placement and format } (estimateDaily)
 *   networkSites: every site's slug (the bundle needs all of them)
 */
export function quote(pricing, order, estimates = {}, networkSites = []) {
  const model = MODEL_IDS.includes(order.model) ? order.model : 'cpm';
  const sites = [...new Set(order.sites || [])];
  const lim = pricing?.limits || { minDays: 7, maxDays: 182 };
  const daily = Object.fromEntries(sites.map((s) => [s, Math.max(0, Math.round(num(estimates[s])))]));
  const problems = [];
  let end = order.end, plan = { model };
  if (model === 'sponsorship') {
    const period = order.period === 'month' ? 'month' : 'week';
    const max = period === 'month' ? num(pricing?.sponsorship?.maxMonths) || 3 : num(pricing?.sponsorship?.maxWeeks) || 8;
    const periods = Math.max(1, Math.min(max, Math.round(num(order.periods) || 1)));
    end = validDay(order.start) ? sponsorshipEnd(order.start, period, periods) : '';
    plan = { model, period, periods, exclusive: true };
  }
  let blockSplit = null;
  if (model === 'block') {
    const sizes = pricing?.impressionBlocks?.sizes || [];
    const size = sizes.includes(Math.round(num(order.block))) ? Math.round(num(order.block)) : sizes[0] || 0;
    const perDay = sites.reduce((n, s) => n + daily[s], 0);
    const need = perDay > 0 ? Math.ceil((size / perDay) * (1 + num(pricing?.impressionBlocks?.slackPercent) / 100)) : Infinity;
    const days = Math.max(lim.minDays, need);
    if (!Number.isFinite(need)) problems.push("These sites haven't been measured yet, so a block can't be scheduled. Pick a measured site, or buy by dates.");
    else if (days > lim.maxDays) problems.push(`At the traffic we measure, ${size.toLocaleString('en-US')} impressions would take about ${need} days, more than ${lim.maxDays}. Pick a smaller block or more sites.`);
    end = validDay(order.start) && Number.isFinite(days) ? addDays(order.start, Math.min(days, lim.maxDays) - 1) : '';
    // Split by traffic; the remainder goes to the busiest site.
    blockSplit = Object.fromEntries(sites.map((s) => [s, perDay > 0 ? Math.floor((size * daily[s]) / perDay) : 0]));
    const busiest = sites.slice().sort((a, b) => daily[b] - daily[a])[0];
    if (busiest && perDay > 0) blockSplit[busiest] += size - Object.values(blockSplit).reduce((n, x) => n + x, 0);
    plan = { model, block: size, discountPercent: blockDiscount(pricing, size) };
  }
  const days = flightDays(order.start, end);
  const weeks = days / 7;
  const premium = model === 'sponsorship' ? { percent: 0, parts: [] } : targetingPremium(pricing, order.targeting);
  const lines = sites.map((site) => {
    const r = rateFor(pricing, site, order.placement);
    const est = Math.round(daily[site] * days);
    const base = { site, cpm: r.cpm, weeklyMinimum: r.weeklyMinimum, dailyImpressions: daily[site] };
    if (model === 'block') {
      const imps = blockSplit[site];
      const c = Math.round((imps / 1000) * r.cpm * 100 * (1 - plan.discountPercent / 100));
      return { ...base, impressions: imps, booked: imps, cpmCents: c, minimumCents: 0, cents: c, basis: 'block' };
    }
    if (model === 'cpc') return { ...base, cpcFloor: r.cpcFloor, impressions: est, booked: 0, cpmCents: 0, minimumCents: 0, cents: 0, basis: 'cpc' };
    if (model === 'sponsorship') {
      const rate = plan.period === 'month' ? r.sponsorMonth : r.sponsorWeek;
      return { ...base, rate, impressions: est, booked: 0, cpmCents: 0, minimumCents: 0, cents: cents(rate * plan.periods), basis: 'sponsorship' };
    }
    const byCpm = cents((est / 1000) * r.cpm);
    const minimum = cents(r.weeklyMinimum * weeks);
    return { ...base, impressions: est, booked: est, cpmCents: byCpm, minimumCents: minimum, cents: Math.max(byCpm, minimum), basis: byCpm >= minimum ? 'cpm' : 'minimum' };
  });
  let subtotal = lines.reduce((n, l) => n + l.cents, 0);
  if (model === 'cpc') {
    const floor = targetedCpcFloorCents(pricing, sites, order.placement, order.targeting);
    const bid = cents(num(order.bid)), budget = cents(num(order.budget));
    const minBudget = cents(num(pricing?.cpc?.minimumBudget)), minDaily = cents(num(pricing?.cpc?.minimumDaily));
    // No daily budget given: the total spread evenly over the days, at least the minimum (and at most the total).
    const dailyCents = order.daily != null && order.daily !== '' && num(order.daily) > 0 ? cents(num(order.daily)) : Math.min(budget, Math.max(minDaily, days > 0 ? Math.ceil(budget / days) : 0));
    if (bid < floor) problems.push(`The bid must be at least ${money(floor)} a click on ${sites.length === 1 ? 'this site' : 'these sites'}.`);
    if (budget < minBudget) problems.push(`The total budget must be at least ${money(minBudget)}.`);
    if (dailyCents < minDaily) problems.push(`The daily budget must be at least ${money(minDaily)}.`);
    if (budget > 0 && dailyCents > budget) problems.push('The daily budget is more than the total budget.');
    if (bid > 0 && bid > budget) problems.push('The bid is more than the total budget.');
    subtotal = budget;
    plan = { model, bidCents: bid, floorCents: floor, budget: { totalCents: budget, dailyCents } };
  }
  const targetingCents = model === 'cpc' ? 0 : Math.round(subtotal * premium.percent / 100);
  const bundle = model !== 'cpc' && networkSites.length > 1 && networkSites.every((s) => sites.includes(s));
  const discount = bundle ? Math.round((subtotal + targetingCents) * (num(pricing?.networkBundle?.percent)) / 100) : 0;
  const afterDiscount = subtotal + targetingCents - discount;
  const abFee = order.ab ? Math.max(Math.round(afterDiscount * num(pricing?.abTest?.percent) / 100), cents(num(pricing?.abTest?.minimum))) : 0;
  const political = order.political ? Math.round(afterDiscount * num(pricing?.political?.surchargePercent) / 100) : 0;
  const total = afterDiscount + abFee + political;
  return {
    model, start: order.start, end, days, weeks: Math.round(weeks * 100) / 100, lines, subtotalCents: subtotal, targetingPercent: premium.percent, targetingParts: premium.parts, targetingCents, bundle, discountCents: discount, abFeeCents: abFee, politicalCents: political,
    totalCents: total, impressions: lines.reduce((n, l) => n + l.impressions, 0), bookedImpressions: lines.reduce((n, l) => n + l.booked, 0), currency: pricing?.currency || 'usd',
    ...(model === 'cpc' ? { bidCents: plan.bidCents, floorCents: plan.floorCents, budgetCents: plan.budget.totalCents, dailyCents: plan.budget.dailyCents, clicks: plan.bidCents > 0 ? Math.floor(plan.budget.totalCents / plan.bidCents) : 0 } : {}),
    ...(model === 'block' ? { block: plan.block, discountPercent: plan.discountPercent } : {}),
    ...(model === 'sponsorship' ? { period: plan.period, periods: plan.periods } : {}),
    plan, problems,
  };
}

/** Whether two campaigns want the same slot on the same days: a shared site, the same format, overlapping page types. */
export function slotsConflict(a, b) {
  if (!a || !b || a.format !== b.format || !(a.start <= b.end && b.start <= a.end)) return false;
  if (!(a.sites || []).some((s) => (b.sites || []).includes(s))) return false;
  const ta = PLACEMENTS[a.placement]?.types || [], tb = PLACEMENTS[b.placement]?.types || [];
  return ta.some((t) => tb.includes(t));
}

/** One line for the money side of a campaign: "CPC $1.00 a click · $200.00 budget, $10.00 a day", and so on. */
export function modelSummary(q) {
  if (!q) return '';
  if (q.model === 'cpc') return `${money(q.bidCents)} a click · ${money(q.budgetCents)} budget, ${money(q.dailyCents)} a day`;
  if (q.model === 'block') return `${Math.round(q.block).toLocaleString('en-US')} impressions${q.discountPercent ? ` · ${q.discountPercent}% block discount` : ''}`;
  if (q.model === 'sponsorship') return `Sponsorship · ${q.periods} ${q.period}${q.periods === 1 ? '' : 's'}, exclusive`;
  return `${Math.round(q.impressions || 0).toLocaleString('en-US')} impressions over ${q.days} days`;
}

export const money = (c) => `$${(Math.round(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Daily viewable impressions we can promise a placement and format on each site, from what was measured: the ad fill
 * counters (slots whose frame loaded, by site, unit and page type: worker/src/ad-fill.js) plus direct ads served in our
 * slots, averaged over the days measured. `rows`: [{site, unit, slotType, n, days}] (n: the total over `days`).
 * `viewRate`: the share of loaded slots that become viewable (measured from direct ads once there are some; 0.6 by default).
 */
export function estimateDaily(rows, { placement, format, viewRate = 0.6 }) {
  const types = PLACEMENTS[placement]?.types || [], units = FORMATS[format]?.units || [];
  const out = {};
  for (const r of rows || []) {
    if (!types.includes(r.slotType) || !units.includes(r.unit) || !(r.days > 0)) continue;
    out[r.site] = (out[r.site] || 0) + (Number(r.n) || 0) / r.days;
  }
  for (const k of Object.keys(out)) out[k] = Math.floor(out[k] * viewRate);
  return out;
}

// ---------- audience shares (worker/src/ad-audience.js counts; the estimates narrowed by targeting) ----------

/** Fewer slot loads than this on a site (over the window) and its targeting isn't estimated: the estimate stays as is. */
export const AUDIENCE_MIN = 300;
/** A ZIP code or city counted fewer times than this in a day is never kept (k-anonymity). */
export const AUDIENCE_FLOOR = 10;

/**
 * The share of a site's measured slot loads for a placement and format that a campaign's targeting keeps, from the
 * aggregate audience counts (worker/src/ad-audience.js audienceMix): page types, then wards or districts within those,
 * then places (states, cities, ZIP codes, combined as if independent). `site`: {loads: [[type, district, unit, n]],
 * geo: {all, region: {}, city: {}, zip: {}}}. Returns {share, types, districts, geo, enough, base}; `enough` false (and
 * share 1) when the site has too little data to say.
 */
export function audienceShare(site, { placement, format, types = [], districts = [], geo = null } = {}) {
  const pTypes = PLACEMENTS[placement]?.types || [], units = FORMATS[format]?.units || [];
  const rows = (site?.loads || []).filter((r) => pTypes.includes(r[0]) && units.includes(r[2]));
  const sum = (list) => list.reduce((n, r) => n + (Number(r[3]) || 0), 0);
  const base = sum(rows);
  const out = { share: 1, types: 1, districts: 1, geo: 1, enough: base >= AUDIENCE_MIN, base };
  if (!out.enough) return out;
  const tSel = types.length ? rows.filter((r) => types.includes(r[0])) : rows;
  out.types = sum(tSel) / base;
  if (districts.length) { const t = sum(tSel); out.districts = t > 0 ? sum(tSel.filter((r) => districts.includes(Number(r[1])))) / t : 0; }
  if (hasGeo(geo)) {
    const g = site?.geo || {}, total = Number(g.all) || 0;
    if (total < AUDIENCE_MIN) out.enough = false;
    else {
      const part = (list, table, key = (x) => x) => Math.min(1, list.reduce((n, x) => n + (Number(table?.[key(x)]) || 0), 0) / total);
      const r = part(geo.regions || [], g.region), c = part(geo.cities || [], g.city, cityKey), z = part(geo.zips || [], g.zip);
      out.geo = 1 - (1 - r) * (1 - c) * (1 - z);
    }
  }
  if (!out.enough) return { ...out, share: 1, types: 1, districts: 1, geo: 1 };
  out.share = out.types * out.districts * out.geo;
  return out;
}

/**
 * Daily estimates narrowed by targeting: {[site]: daily} from {[site]: daily} (estimateDaily) and the audience mix
 * ({sites: {[slug]: site}}), with each site's audienceShare. A site without enough data keeps its estimate.
 */
export function targetedDaily(estimates, mix, order) {
  const out = {}, shares = {};
  for (const [s, n] of Object.entries(estimates || {})) {
    const sh = audienceShare(mix?.sites?.[s], order);
    shares[s] = sh;
    out[s] = Math.floor((Number(n) || 0) * sh.share);
  }
  return { daily: out, shares };
}

// ---------- campaign states ----------

/**
 * A campaign's states. A campaign is submitted with its quote and waits for payment (Stripe Checkout, or an invoice);
 * paid, it goes to review; staff approve it (an invoiced one can be approved before it's paid, and runs once it is) or
 * reject it with a reason. Approved campaigns run between their dates while paid; staff or the advertiser can pause and
 * resume. A refund is a note (and an amount) on the campaign, not a state.
 */
export const STATES = ['pending_payment', 'in_review', 'approved', 'paused', 'rejected', 'canceled'];
export const STATE_LABELS = { pending_payment: 'Pending payment', in_review: 'In review', approved: 'Approved', paused: 'Paused', rejected: 'Rejected', canceled: 'Canceled' };
const T = {
  paid: { from: ['pending_payment', 'in_review', 'approved', 'paused'], staff: false },
  approve: { from: ['pending_payment', 'in_review', 'paused'], staff: true },
  reject: { from: ['pending_payment', 'in_review', 'approved', 'paused'], staff: true, needs: 'reason' },
  pause: { from: ['approved'], staff: false },
  resume: { from: ['paused'], staff: false },
  cancel: { from: ['pending_payment', 'in_review'], staff: false },
  refund: { from: ['pending_payment', 'in_review', 'approved', 'paused', 'rejected', 'canceled'], staff: true, needs: 'note' },
};
export const ACTIONS = Object.keys(T);

/**
 * The fields a campaign gets from `action`, or {error}. `who`: 'staff' | 'advertiser' | 'system' (payments).
 * `c`: {status, paid}. `input`: {reason, note, amountCents}.
 */
export function transition(c, action, who, input = {}) {
  const t = T[action];
  if (!t) return { error: 'unknown action' };
  if (action === 'paid' && who !== 'system' && who !== 'staff') return { error: 'only a payment or staff can mark a campaign paid' };
  if (t.staff && who !== 'staff') return { error: 'staff only' };
  if (!t.from.includes(c.status)) return { error: `can't ${action === 'paid' ? 'mark paid' : action} a campaign that is ${(STATE_LABELS[c.status] || c.status).toLowerCase()}` };
  if (t.needs === 'reason' && !String(input.reason || '').trim()) return { error: 'a reason is required' };
  if (t.needs === 'note' && !String(input.note || '').trim()) return { error: 'a note is required' };
  switch (action) {
    case 'paid': return { paid: 1, status: c.status === 'pending_payment' ? 'in_review' : c.status };
    case 'approve': return { status: 'approved' };
    case 'reject': return { status: 'rejected', review_reason: String(input.reason).trim().slice(0, TEXT_LIMITS.reason) };
    case 'pause': return { status: 'paused' };
    case 'resume': return { status: 'approved' };
    case 'cancel': return { status: 'canceled' };
    case 'refund': {
      const amount = Math.max(0, Math.round(Number(input.amountCents) || 0));
      return { refund_note: String(input.note).trim().slice(0, TEXT_LIMITS.reason), refunded_cents: amount };
    }
    default: return { error: 'unknown action' };
  }
}

/** Whether a campaign may serve today: approved, paid, and today within its dates. */
export const isLive = (c, today = isoDay()) => c.status === 'approved' && !!c.paid && c.start_day <= today && today <= c.end_day;

/** What's owed back for undelivered impressions: the total times the undelivered share (none once fully delivered). */
export const proratedRefund = (totalCents, booked, delivered) => (booked > 0 ? Math.max(0, Math.round(totalCents * (1 - Math.min(1, delivered / booked)))) : 0);

/**
 * Today's cap on viewable impressions for even delivery: what's left of the booking spread over the days left (today
 * included). No booking (a site we couldn't measure yet): no cap.
 */
export function dailyCap({ booked, delivered, today, end }) {
  if (!(booked > 0)) return Infinity;
  const left = Math.max(1, flightDays(today, end));
  return Math.max(0, Math.ceil((booked - delivered) / left));
}

// ---------- serving ----------

/** Pages that never carry ads: the homepage, About, Contact, Privacy, Terms, sign-in and account, the founder's letter. */
export function excludedPath(path) {
  const p = String(path || '/').toLowerCase().replace(/\/+$/, '') || '/';
  if (p === '/' || /^\/[a-z]{2}(?:-[a-z]+)?$/.test(p)) return true;   // a home, in any language
  if (/^(?:\/[a-z]{2}(?:-[a-z]+)?)?\/(?:about|acerca|o-nas|contact|contacto|kontakt|privacy|privacidad|prywatnosc|terms|terminos|warunki|signin|sign-in|account|dashboard|advertise)(?:\/|$)/.test(p)) return true;
  const omb = /^\/on-my-block\/([a-z0-9-]+)$/.exec(p);
  return !!(omb && NO_AD_ARTICLE.test(omb[1]));
}

/** What a slot's page is, for targeting: its type, its ward or district number, its article. Never the path itself. */
export function pageContext(path, { article = false } = {}) {
  const p = String(path || '/');
  const d = /^\/(?:ward|district)\/(\d{1,3})(?:\/|$)/i.exec(p);
  const a = /^\/on-my-block\/([a-z0-9-]+)\/?$/.exec(p);
  return { type: slotTypeOf(p, { article }), district: d ? Number(d[1]) : null, article: a ? a[1] : null };
}

// ---------- geo targeting (the ad engine, worker/src/ad-engine.js) ----------

/** The states an advertiser can pick (USPS codes, as Cloudflare's request.cf.regionCode gives them). */
export const US_STATES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};
export const GEO_LIMITS = { zips: 500, cities: 100, regions: 51 };
const cityKey = (s) => String(s ?? '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * A campaign's location targeting, cleaned: {regions: ['IL'], cities: ['chicago'], zips: ['60614']}. ZIPs: five digits
 * each, from a list or pasted text (commas, spaces, new lines); cities as names; states as USPS codes or names.
 */
export function cleanGeo(g = {}) {
  const byName = Object.fromEntries(Object.entries(US_STATES).map(([k, v]) => [v.toLowerCase(), k]));
  const list = (x) => (Array.isArray(x) ? x : String(x ?? '').split(/[\n,;]+/)).map((s) => String(s).trim()).filter(Boolean);
  const zips = [...new Set(list(g.zips).join(' ').split(/\s+/).map((z) => z.replace(/[^\d]/g, '').slice(0, 5)).filter((z) => /^\d{5}$/.test(z)))].slice(0, GEO_LIMITS.zips);
  const cities = [...new Set(list(g.cities).map(cityKey).filter((c) => c.length >= 2 && c.length <= 60))].slice(0, GEO_LIMITS.cities);
  const regions = [...new Set(list(g.regions).map((r) => (US_STATES[r.toUpperCase()] ? r.toUpperCase() : byName[r.toLowerCase()])).filter(Boolean))].slice(0, GEO_LIMITS.regions);
  return { regions, cities, zips };
}
export const hasGeo = (g) => !!(g && (g.regions?.length || g.cities?.length || g.zips?.length));
/** A campaign's location targeting in words ("Illinois · chicago, evanston · 3 ZIP codes"), or '' for everywhere. */
export const geoText = (g) => (hasGeo(g) ? [g.regions?.map((r) => US_STATES[r] || r).join(', '), g.cities?.join(', '), g.zips?.length ? `${g.zips.length} ZIP code${g.zips.length === 1 ? '' : 's'}${g.zips.length <= 5 ? ` (${g.zips.join(', ')})` : ''}` : ''].filter(Boolean).join(' · ') : '');

/**
 * Whether a reader's approximate location (from the request's IP: Cloudflare's request.cf country, regionCode, city,
 * postalCode) is in a campaign's targeted states, cities or ZIP codes (any of them). No targeting: everyone. Used only
 * to match the ad; never stored or logged.
 */
export function geoMatches(geo, loc) {
  if (!hasGeo(geo)) return true;
  if (!loc || loc.country !== 'US') return false;
  if (geo.zips?.length && loc.postalCode && geo.zips.includes(String(loc.postalCode).slice(0, 5))) return true;
  if (geo.regions?.length && loc.region && geo.regions.includes(String(loc.region).toUpperCase())) return true;
  if (geo.cities?.length && loc.city && geo.cities.includes(cityKey(loc.city))) return true;
  return false;
}

/**
 * Whether a campaign (as the serving list has it: {sites, placement, format, targeting: {types, districts, articles, geo}})
 * fits a slot: {site, type, district, article, unit, geo?}. Targeting narrows the placement; an empty list means any.
 * A campaign with location targeting never matches a slot whose reader's location isn't known (ctx.geo missing).
 */
export function campaignMatches(c, ctx) {
  if (hasGeo(c?.targeting?.geo) && !geoMatches(c.targeting.geo, ctx.geo)) return false;
  if (!c || !(c.sites || []).includes(ctx.site)) return false;
  const types = PLACEMENTS[c.placement]?.types || [];
  if (!types.includes(ctx.type)) return false;
  if (!FORMATS[c.format]?.units.includes(ctx.unit)) return false;
  const t = c.targeting || {};
  if (t.types?.length && !t.types.includes(ctx.type)) return false;
  if (t.districts?.length && !t.districts.includes(ctx.district)) return false;
  if (t.articles?.length && !t.articles.includes(ctx.article)) return false;
  return true;
}

/** The image a leaderboard set shows in a slot of `unit`: that size, else the next smaller one it has. */
export function imageFor(creative, unit) {
  const imgs = creative?.images || {};
  if (imgs[unit]) return { size: unit, ...imgs[unit] };
  const order = FORMATS.leaderboard.sizes;
  if (order.includes(unit)) for (const s of order.slice(order.indexOf(unit) + 1)) if (imgs[s]) return { size: s, ...imgs[s] };
  return null;
}

/** One live campaign for a slot: those that match and have an image for it, picked at random (`rand` for the tests). */
export function pickDirect(list, ctx, rand = Math.random) {
  const all = (list || []).filter((c) => campaignMatches(c, ctx) && Object.values(c.creatives || {}).some((cr) => creativeFits(c, cr, ctx.unit)));
  // A sponsorship owns its slot: it wins over every other campaign there.
  const sponsor = all.filter(isSponsorship);
  const fits = sponsor.length ? sponsor : all;
  return fits.length ? fits[Math.floor(rand() * fits.length) % fits.length] : null;
}
/** Whether a campaign (as the snapshot holds it, with its plan in `pricing`) is an exclusive sponsorship. */
export const isSponsorship = (c) => c?.pricing?.model === 'sponsorship' || c?.sponsorship === true;

export const AB_KEY = 'cp_ad_ab';
let memoryVariant = null;
/**
 * The reader's half for A/B tests: 'a' or 'b', drawn once and kept in this browser's localStorage (a first-party
 * value, no identifier: the same letter for every campaign), so a reader sees one creative for a campaign's whole run.
 * Without storage, one draw per page.
 */
export function abVariant(storage = typeof localStorage !== 'undefined' ? localStorage : null, rand = Math.random) {
  try {
    const v = storage?.getItem(AB_KEY);
    if (v === 'a' || v === 'b') return v;
    const pick = rand() < 0.5 ? 'a' : 'b';
    storage?.setItem(AB_KEY, pick);
    if (storage) return pick;
  } catch { /* storage blocked */ }
  return (memoryVariant ||= rand() < 0.5 ? 'a' : 'b');
}
/** The creative a reader sees: their half's, or 'a' when the campaign isn't an A/B test. */
export const variantFor = (c, half) => (c.ab && c.creatives?.b ? half : 'a');

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));

/**
 * A direct ad's markup inside a slot's box: the creative as an image link (rel sponsored, a new tab), or the native card,
 * plus the "Paid for by" line of a political ad. Our own markup: an image and text, no advertiser script. `href`: our
 * click redirect (/ads/click/<id>?…), or '#' in the builder's preview.
 */
/** Whether a creative can fill a slot of `unit`: a compiled design any unit of its format, a native card its image, a banner an image of that size (or smaller). */
export const creativeFits = (c, cr, unit) => (cr?.kind === 'design' ? !!cr.frame && !!FORMATS[c.format]?.units.includes(unit)
  : cr?.kind === 'promo' ? !!cr.frame && PROMO_UNITS.includes(unit) && !!FORMATS[c.format]?.units.includes(unit)
    : FORMATS[c.format]?.kind === 'native' ? !!cr?.images?.native : !!imageFor(cr, unit));

export function directAdHtml(c, creative, { unit, href }) {
  const paid = c.political && c.paidForBy ? `<p class="dad__paid">Paid for by ${esc(c.paidForBy)}</p>` : '';
  const link = (inner, cls) => `<a class="${cls}" href="${esc(href)}" target="_blank" rel="sponsored noopener">${inner}</a>`;
  // A compiled design (the ad designer): only in our sandboxed frame, which may open the click in a new tab and nothing else.
  if (creative?.kind === 'design' && creative.frame) {
    const s = sizeOf(unit) || { width: 640, height: 300 };
    const src = `${creative.frame}&z=${encodeURIComponent(unit)}&href=${encodeURIComponent(href)}`;
    return `<div class="dad dad--design"><iframe class="dad__frame" src="${esc(src)}" title="${AD_LABEL}" width="${s.width}" height="${s.height}" sandbox="allow-popups allow-popups-to-escape-sandbox" loading="lazy" referrerpolicy="strict-origin-when-cross-origin" style="border:0;max-width:100%"></iframe>${paid}</div>`;
  }
  // The house promo (worker/src/house-ad.js): our own script-free document from this site's host, named for its city.
  // Same-origin in the sandbox only so it can use our own font; it can run no script either way.
  if (creative?.kind === 'promo' && creative.frame) {
    const s = sizeOf(unit);
    const src = `${creative.frame}&z=${encodeURIComponent(unit)}&href=${encodeURIComponent(href)}`;
    const dims = s ? `width="${s.width}" height="${s.height}" style="border:0;max-width:100%;display:block"` : 'width="640" height="250" style="border:0;width:100%;max-width:640px;display:block"';
    return `<div class="dad dad--promo dad--${esc(unit)}"><iframe class="dad__frame" src="${esc(src)}" title="${esc(creative.alt || AD_LABEL)}" ${dims} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>${paid}</div>`;
  }
  if (FORMATS[c.format]?.kind === 'native') {
    const img = creative.images?.native;
    const inner = `${img ? `<img class="dad__img" src="${esc(img.src)}" alt="" width="${img.width}" height="${img.height}" loading="lazy" decoding="async">` : ''}`
      + `<span class="dad__text"><b class="dad__headline">${esc(creative.headline)}</b>${creative.body ? `<span class="dad__body">${esc(creative.body)}</span>` : ''}`
      + `<span class="dad__by">${esc(creative.sponsor || displayHost(creative.url))}</span></span>`;
    return `<div class="dad dad--native">${link(inner, 'dad__card')}${paid}</div>`;
  }
  const img = imageFor(creative, unit);
  if (!img) return '';
  const s = sizeOf(img.size);
  return `<div class="dad dad--banner dad--${esc(img.size)}">${link(`<img class="dad__img" src="${esc(img.src)}" alt="${esc(creative.alt || creative.sponsor || 'Advertisement')}" width="${s.width}" height="${s.height}" loading="lazy" decoding="async">`, 'dad__link')}${paid}</div>`;
}

/** Our click redirect for a creative shown in a slot: /ads/click/<campaign>?v=<variant>&z=<unit>&t=<page type>. */
export const clickHref = (c, variant, unit, type, tok = '') => `/ads/click/${encodeURIComponent(c.id)}?v=${variant}&z=${encodeURIComponent(unit)}&t=${encodeURIComponent(type)}${tok ? `&k=${encodeURIComponent(tok)}` : ''}`;

export const EVENT_KINDS = ['serve', 'view'];
/** A slot is viewable once at least half of it has been on screen for a second (the IAB's display standard). */
export const VIEWABLE = { ratio: 0.5, ms: 1000 };

// ---------- partner sites (outside publishers' sites carrying our tag: worker/src/ad-publishers.js) ----------

/** A partner site's slug, as campaigns, flights, tokens and counters name it: "x_" and 10 hex. */
export const PARTNER_SLUG = /^x_[0-9a-f]{10}$/;
export const isPartnerSlug = (s) => PARTNER_SLUG.test(String(s || ''));
/** A zone (one ad space on a partner site): "z_" and 10 hex. */
export const ZONE_ID = /^z_[0-9a-f]{10}$/;
/** A partner page's type for targeting and counters. */
export const PARTNER_TYPE = 'partner';
/** The publisher's ads.txt line that verifies a domain: our domain, their account token, DIRECT. */
export const adsTxtLine = (token) => `citypolitics.org, ${token}, DIRECT`;
/** The DNS TXT record that verifies a domain instead. */
export const dnsTxtValue = (token) => `citypolitics-verify=${token}`;
/** A bare host name a publisher may register: lowercase, letters, digits, hyphens and dots, a real TLD. */
export function cleanDomain(raw) {
  let s = String(raw || '').trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '').replace(/[/?#].*$/, '').replace(/^www\./, '').replace(/\.$/, '');
  if (s.length > 253 || !/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(s)) return null;
  if (/(^|\.)(citypolitics\.org|localhost|local|internal|test|example|invalid)$/.test(s)) return null;
  return s;
}
/** Whether a page's host is a registered domain or one of its subdomains. */
export const hostOnDomain = (host, domain) => { const h = String(host || '').toLowerCase().replace(/\.$/, ''); return !!domain && (h === domain || h.endsWith(`.${domain}`)); };

/**
 * A zone's tag for the publisher's page: a placeholder of the slot's size holding a no-script fallback (a plain link
 * and image), and our async loader, which draws the ad in a sandboxed frame from citypolitics.org inside it.
 */
export function zoneTag(zone, origin = 'https://citypolitics.org') {
  const f = FORMATS[zone.format] || FORMATS['300x250'];
  const s = sizeOf(f.units[0]) || { width: 300, height: 250 };
  const h = zone.format === 'native' ? 320 : s.height;
  const w = zone.format === 'native' ? 640 : s.width;
  const id = esc(zone.id);
  return `<!-- City Politics ad: ${esc(zone.name || zone.id)} -->
<ins class="cp-ad" data-zone="${id}" style="display:block;max-width:100%;width:${w}px;height:${h}px;margin:0 auto"><noscript><span style="position:relative;display:inline-block;max-width:100%;line-height:0"><a href="${origin}/ads/nojs/click/${id}" rel="sponsored noopener" target="_blank"><img src="${origin}/ads/nojs/img/${id}" width="${s.width}" height="${s.height}" alt="Advertisement" style="max-width:100%;height:auto;border:0"></a><span style="position:absolute;top:0;right:0;padding:1px 5px;font:10px/14px system-ui,sans-serif;background:rgba(255,255,255,.88);color:#555">Advertisement</span></span></noscript></ins>
<script async src="${origin}/ads/tag.js"></script>`;
}
